import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import { Dialog } from 'components/dialog';
import { UpdateProfile } from 'components/settings/UpdateProfile';
import { ActivityIndicator } from 'components/user/ActivityIndicator';
import { Avatar } from 'components/user/Avatar';
import { useCurrentUserContext } from 'contexts/currentUser';

import type { ActiveUserAvatar_user } from './__generated__/index.graphql';

type Props = {
  user: ActiveUserAvatar_user;
  editable?: boolean;
} & Pick<
  ComponentProps<typeof Avatar>,
  | 'size'
  | 'rounded'
  | 'largePictureUrl'
  | 'placeholderUrl'
  | 'hideChampionshipFrame'
>;

const UpdateProfileWrapper = styled.div`
  padding: var(--double-unit);
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;

const extraSmallActivityIndicatorStyle = {
  '--radius': '3px',
  // harcoded value to work both on mobile where font-size is 12 and desktop where it's 14
  '--offset-right': 'calc(1em - 12px)',
  '--offset-bottom': 'calc(1em - 9px)',
  '--gap': '1px',
} as React.CSSProperties;

const roundedCSStyle = {
  '--offset-right': '6px',
  '--offset-bottom': '6px',
} as React.CSSProperties;

export const ActiveUserAvatar = ({
  user,
  editable = true,
  rounded,
  size,
  ...avatarProps
}: Props) => {
  const { currentUser } = useCurrentUserContext() || {};
  const [openUpdatingPicture, setOpenUpdatingPicture] = useState(false);

  const otherUser = currentUser?.slug !== user.slug;

  // disable edit mode on those extraSmall avatars
  const withoutEdit = !editable || size <= 2;

  // only support mobile click when we're not opening the edit modal
  const disableClick = !(withoutEdit || otherUser);

  const roundedStyle = rounded ? roundedCSStyle : undefined;

  const avatar = (
    <ActivityIndicator
      user={user}
      disableClick={disableClick}
      style={size <= 2 ? extraSmallActivityIndicatorStyle : roundedStyle}
    >
      <Avatar user={user} size={size} rounded={rounded} {...avatarProps} />
    </ActivityIndicator>
  );

  if (otherUser || withoutEdit) {
    return avatar;
  }

  return (
    <>
      <button type="button" onClick={() => setOpenUpdatingPicture(true)}>
        {avatar}
      </button>
      <Dialog
        maxWidth="sm"
        open={openUpdatingPicture}
        onClose={() => setOpenUpdatingPicture(false)}
        hideHeader
      >
        {({ CloseButton }) => (
          <UpdateProfileWrapper>
            <CloseButtonWrapper>
              <CloseButton onClose={() => setOpenUpdatingPicture(false)} />
            </CloseButtonWrapper>
            <UpdateProfile onSuccess={() => setOpenUpdatingPicture(false)} />
          </UpdateProfileWrapper>
        )}
      </Dialog>
    </>
  );
};

ActiveUserAvatar.fragments = {
  user: gql`
    fragment ActiveUserAvatar_user on PublicUserInfoInterface {
      slug
      ...ActivityIndicator_user
      ...Avatar_publicUserInfoInterface
    }
    ${ActivityIndicator.fragments.user}
    ${Avatar.fragments.publicUserInfoInterface}
  ` as TypedDocumentNode<ActiveUserAvatar_user>,
};
